import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { SettingsProvider } from "@/context/SettingsContext";

// Pages
import HomePage from "@/pages/HomePage";
import CatalogPage from "@/pages/CatalogPage";
import PropertyPage from "@/pages/PropertyPage";
import ContactPage from "@/pages/ContactPage";
import DocumentsPage from "@/pages/DocumentsPage";
import GestionPage from "@/pages/GestionPage";
import AgencePage from "@/pages/AgencePage";
import EstimationPage from "@/pages/EstimationPage";
import LegalPage from "@/pages/LegalPage";
import AdminOffers from "@/pages/admin/AdminOffers";
import ScrollToTop from "@/components/ScrollToTop";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProperties from "@/pages/admin/AdminProperties";
import AdminDocuments from "@/pages/admin/AdminDocuments";
import AdminContacts from "@/pages/admin/AdminContacts";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminRgpd from "@/pages/admin/AdminRgpd";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    if (response.data.otp_required) return response.data;
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const verifyOtp = async (challenge_id, code) => {
    const response = await axios.post(`${API}/auth/verify-otp`, { challenge_id, code });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, verifyOtp, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F9]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <SettingsProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/catalogue" element={<CatalogPage />} />
              <Route path="/gestion" element={<GestionPage />} />
              <Route path="/agence" element={<AgencePage />} />
              <Route path="/estimation" element={<EstimationPage />} />
              <Route path="/mentions-legales" element={<LegalPage type="legal" />} />
              <Route path="/politique-confidentialite" element={<LegalPage type="privacy" />} />
              <Route path="/bien/:id" element={<PropertyPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/documents" element={<DocumentsPage />} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/biens" element={<ProtectedRoute><AdminProperties /></ProtectedRoute>} />
              <Route path="/admin/documents" element={<ProtectedRoute><AdminDocuments /></ProtectedRoute>} />
              <Route path="/admin/contacts" element={<ProtectedRoute><AdminContacts /></ProtectedRoute>} />
              <Route path="/admin/parametres" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
              <Route path="/admin/offres" element={<ProtectedRoute><AdminOffers /></ProtectedRoute>} />
              <Route path="/admin/rgpd" element={<ProtectedRoute><AdminRgpd /></ProtectedRoute>} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </div>
      </AuthProvider>
    </SettingsProvider>
  );
}

export default App;
